// The acknowledged watermark is local deduplication state, not a server cursor.
export function createReadAcknowledgement(write, refreshed, report, {intervalMs = 0, retryTransient = false} = {}) {
    let lastWrite = -Infinity, timer = null, wake = null;
    let failures = 0;
    let completed = 0, attempted = 0, desired = 0, running = null, disposed = false;
    const waitForInterval = delay => new Promise(resolve => { wake = resolve; timer = setTimeout(resolve, delay); });
    async function drain() {
        while (!disposed && desired > Math.max(completed, attempted)) {
            const delay = Math.max(0, lastWrite + intervalMs - Date.now());
            if (delay) await waitForInterval(delay);
            timer = null; wake = null;
            if (disposed) return;
            const target = desired;
            let result;
            attempted = target;
            try {
                lastWrite = Date.now();
                result = await write(target);
                if (disposed) return;
                completed = Math.max(completed, target);
                failures = 0;
            } catch (error) {
                if (!disposed && error?.name !== 'AbortError') report(error);
                if (!disposed && retryTransient && error?.name !== 'AbortError'
                    && (!error.status || [429, 500, 502, 503, 504].includes(error.status))) {
                    const delay = Math.min(15000, 2000 * (2 ** Math.min(failures++, 3)) * (0.8 + Math.random() * 0.4));
                    await waitForInterval(Math.max(delay, error.retryAfterMs || 0));
                    timer = null; wake = null;
                    attempted = completed;
                    continue;
                }
                if (desired > target) continue;
                return;
            }
            // A summary failure must never turn a successful write into a retry.
            try { await refreshed(result); } catch (error) { if (!disposed && error?.name !== 'AbortError') report(error); }
        }
    }
    return {
        request(target, {retry = false} = {}) {
            if (disposed || !Number.isSafeInteger(target) || target <= 0) return Promise.resolve();
            desired = Math.max(desired, target);
            if (retry && !running) attempted = completed;
            if (!running && desired > Math.max(completed, attempted)) running = drain().finally(() => { running = null; });
            return running || Promise.resolve();
        },
        dispose() { disposed = true; clearTimeout(timer); wake?.(); },
    };
}
